<template>
    <div class="modal-container">
        <form-wrapper class="modal-form" :validator="$v.rateDetail">
            <header class="modal-header">
                <div class="modal-header__right">
                    <md-button class="md-simple md-just-icon md-round" @click.stop="$modal.hide">
                        <md-icon>clear</md-icon>
                    </md-button>
                </div>
            </header>

            <section class="modal-section">
                <div class="section-heading">
                    <div class="section-override">
                        <md-switch v-model="rateDetail.overrideRule">
                        Override
                        </md-switch>
                    </div>
                    <span class="section-heading__badge">Timeframe</span>
                    <p class="section-heading__copy">Choose when the rule is active.</p>
                </div>
                <div class="section-card section-card--wide">
                    <rule-valid-days
                        :current-days="rateDetail.validForWeekDays"
                        :from-date="rateDetail.validFromDate"
                        :to-date="rateDetail.validToDate"
                        @daysSelected="handleDaysSeleceted"
                        ref="ruleValidDays"
                    />
                </div>
            </section>

            <section class="modal-section">
                <div class="section-heading">
                    <span class="section-heading__badge">Charging</span>
                    <p class="section-heading__copy">Set base charges, per-distance rate and inclusions.</p>
                </div>
                <div class="section-card section-card--compact">
                    <div class="charging-grid">
                        <div class="charging-group">
                            <h5 class="charging-group__title">Flag fall</h5>
                            <p class="charging-group__hint">Optional one-off amount before distance billing begins.</p>
                            <form-group name="distanceFirstFlagfall" label="Flag Fall">
                                <rule-operator
                                    v-if="$_nullChecker(rateDetail.distanceFirstFlagfall)"
                                    v-model="rateDetail.distanceFirstFlagfall.operator"
                                    :value="rateDetail.distanceFirstFlagfall.operator"
                                />
                                <md-input v-model.number="rateDetail.distanceFirstFlagfall.value" />
                            </form-group>
                        </div>

                        <div class="charging-separator" aria-hidden="true"></div>

                        <div class="charging-group">
                            <h5 class="charging-group__title">Per distance</h5>
                            <p class="charging-group__hint">Rate applied for distance travelled beyond inclusions.</p>
                            <form-group name="distanceRatePerKilometre" :label="metricValue">
                                <rule-operator
                                    v-if="$_nullChecker(rateDetail.distanceRatePerKilometre)"
                                    v-model="rateDetail.distanceRatePerKilometre.operator"
                                    :value="rateDetail.distanceRatePerKilometre.operator"
                                />
                                <md-input v-model.number="rateDetail.distanceRatePerKilometre.value" />
                            </form-group>
                        </div>

                        <div class="charging-separator" aria-hidden="true"></div>

                        <div class="charging-group">
                            <h5 class="charging-group__title">Inclusions</h5>
                            <p class="charging-group__hint">Define included distance and minimum charges.</p>
                            <div class="field-pair">
                                <form-group name="includedDistance" label="Included Distance">
                                    <rule-operator
                                        v-if="$_nullChecker(rateDetail.includedDistance)"
                                        v-model="rateDetail.includedDistance.operator"
                                        :value="rateDetail.includedDistance.operator"
                                    />
                                    <md-input v-model.number="rateDetail.includedDistance.value" />
                                </form-group>
                                <form-group name="distanceMinimumCharge" label="Minimum Charge">
                                    <rule-operator
                                        v-if="$_nullChecker(rateDetail.distanceMinimumCharge)"
                                        v-model="rateDetail.distanceMinimumCharge.operator"
                                        :value="rateDetail.distanceMinimumCharge.operator"
                                    />
                                    <md-input v-model.number="rateDetail.distanceMinimumCharge.value" />
                                </form-group>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <footer class="modal-footer">
                <div class="modal-footer__actions">
                    <md-button class="md-default dialog-button" @click.stop="$modal.hide">Cancel</md-button>
                    <md-button class="md-primary dialog-button" @click="create()">
                        {{ isUpdate ? 'Update' : 'Create' }}
                    </md-button>
                </div>
            </footer>
        </form-wrapper>
    </div>
</template>

<script>
import { RATE_RULE } from '@/utils/defaults';
import { GeneralMixin, RatesEngineMixin } from '@/mixins';
import { handleRequests, showErrorMessage } from '@/helpers';
import { mapGetters } from 'vuex';
import { RATE_CHARGING_TYPES } from '@/utils/constants';
import { decimal } from 'vuelidate/lib/validators';
import { RuleValidDays } from '@/components';
import RuleOperator from './RuleOperator';

export default {
    name: 'CreateDistanceRuleContent',
    components: {
        RuleValidDays,
        RuleOperator
    },
    mixins: [GeneralMixin, RatesEngineMixin],
    props: {
        rateDetail: {
            type: Object,
            default: () => Object.assign({}, RATE_RULE()),
        },
        resolve: {
            type: Function,
            default: () => {}
        },
        listLength: {
            type: Number,
            default: 0
        },
        isUpdate: {
            type: Boolean,
            default: false
        },
        customerId: {
            type: Number,
            default: null
        },
        rateGroupId: {
            type: Number,
            default: null
        },
        driverUserId: {
            type: Number,
            default: null
        },
        driverRuleCreation: {
            type: Boolean,
            default: false
        },
        metricValue: {
            type: String,
            default: null
        },
        teamRegionId: {
            type: Number,
            default: null
        }
    },
    created() {
        this.$_ensureChargeField('includedDistance');
        this.$_ensureChargeField('distanceRatePerKilometre');
        this.$_ensureChargeField('distanceFirstFlagfall');
        this.$_ensureChargeField('distanceMinimumCharge');
    },
    data() {
        return {
            ruleData: {
                type: null,
                rule: '',
                customerId: null
            },
            rateChargingTypes: RATE_CHARGING_TYPES
        };
    },
    computed: {
        ...mapGetters({
            user: 'user/user'
        })
    },
    validations: {
        rateDetail: {
            distanceFirstFlagfall: {
                value: {
                    decimal
                }
            },
            distanceRatePerKilometre: {
                value: {
                    decimal
                }
            },
            distanceMinimumCharge: {
                value: {
                    decimal
                }
            },
            includedDistance: {
                value: {
                    decimal
                }
            }
        }
    },
    methods: {
        validateData() {
            if (this.$v.$invalid) {
                return false;
                // eslint-disable-next-line no-else-return
            } else if (
                this.$v.rateDetail.distanceFirstFlagfall.value.$model === null &&
                this.$v.rateDetail.distanceRatePerKilometre.value.$model === null &&
                this.$v.rateDetail.distanceMinimumCharge.value.$model === null &&
                this.$v.rateDetail.includedDistance.value.$model === null
            ) {
                return false;
            }

            return true;
        },
        async create() {
            const ruleDaysValid = this.$refs.ruleValidDays.validate();
            if (!ruleDaysValid) {
                return;
            }

            if (this.isUpdate) {
                this.updateRuleDetails();
                return;
            }

            if (!this.validateData()) {
                showErrorMessage(this, 'Rule must have valid data', null);
                return;
            }

            this.$_handleLoaderState(true, 'PROCESSING...');
            const chargeType = this.rateChargingTypes.find((x) => x.key.toLowerCase() === this.$route.params.type);
            const obj = this.$_getRateRuleData(
                chargeType,
                this.user.publicUserId,
                this.rateDetail,
                this.customerId,
                this.listLength,
                this.rateGroupId,
                this.driverRuleCreation,
                this.driverUserId,
                this.teamRegionId
            );

            const endpoint = '/api/rates/create';
            const payload = {
                method: 'post',
                data: obj
            };

            try {
                const response = await handleRequests(endpoint, payload);

                if (response.status === 200) {
                    this.$notify({
                        message: 'Successfully created rule.',
                        type: 'success'
                    });

                    this.resolve({
                        record: response.data
                    });
                } else {
                    showErrorMessage(this, 'Failed creating rule.', null);
                }
            } catch (error) {
                showErrorMessage(this, error.data, null);
            }

            this.$_handleLoaderState(false);
        },
        async updateRuleDetails() {
            if (!this.validateData()) {
                showErrorMessage(this, 'Rule must have valid data', null);
                return;
            }

            this.$_handleLoaderState(true, 'UPDATING...');

            const rateId = this.rateDetail.rateRulesId;
            this.rateDetail.condition = null;
            this.rateDetail.currency = null;
            this.rateDetail.priority = null;
            this.rateDetail.rateRulesId = null;

            const obj = {
                rateRulesId: rateId,
                rule: JSON.stringify(this.rateDetail)
            };

            const endpoint = '/api/rates/updateRate';
            const payload = {
                method: 'post',
                data: obj
            };

            try {
                const response = await handleRequests(endpoint, payload);

                if (response.status === 200) {
                    this.$notify({
                        message: 'Successfully updated rule.',
                        type: 'success'
                    });

                    this.resolve({
                        record: response.status
                    });
                } else {
                    showErrorMessage(this, 'Failed updating rule.', null);
                }
            } catch (error) {
                showErrorMessage(this, 'Failed updating rule.', null);
            }

            this.$_handleLoaderState(false);
        },
        handleDaysSeleceted(value) {
            this.rateDetail = Object.assign(this.rateDetail, value);
        }
    }
};
</script>

<style lang="scss" scoped>
@import './create-rule-content.shared.scss';
</style>
